import {
  Suspense,
  createContext,
  use,
  useEffect,
  useLayoutEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import {
  useMatches,
  useNavigate,
  useRouter,
  useSearch,
  type AnyRoute,
  type AnyRouter,
  type FileRoutesByPath,
  type ResolveParams,
} from '@tanstack/react-router'
import type { RootSearch } from '@/routes/__root'
import { DialogEnterContext, createEnterHandover, type EnterHandover } from '@/components/ui/dialog-surface'

/**
 * The modal slot: dialogs are routes under `routes/@modal/`, and the one open is the slot PATH in `?@modal=`
 * (`?@modal=/jobs/5/edit`, on the wire `?%40modal=%2Fjobs%2F5%2Fedit`). It is the shape of TanStack Router's
 * parallel-route RFC (#6302): same key, same files, same fully qualified `to` (`/@modal/jobs/$jobId/edit`), so
 * adopting the real slots is mostly a rename — docs/notes/app/dialog-routes.md.
 */
export const MODAL_KEY = '@modal'
const PREFIX = '/@modal'

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    // The other routes' per-page chrome keys (`pageTitle`, `hideFooter`), which each shell reads with its own type.
    [key: string]: unknown
    /**
     * The route that must be matched for this slot route to render: the RFC's "scoped slot". A dialog that reads
     * its parent page's loaded list names that page; the signed-in dialogs name `/_app`. Omitted = anywhere.
     */
    slotScope?: string
    /** Which `<ModalSlotOutlet name>` renders it. Omitted = the root's. */
    slotOutlet?: 'userMenu'
  }
}

/** Every slot route's fully qualified path, as `<Link to>` will spell it once the RFC lands. */
export type ModalTo = Extract<FileRoutesByPath[keyof FileRoutesByPath]['fullPath'], `${typeof PREFIX}/${string}`>

type ModalParams<T extends ModalTo> = { [K in keyof ResolveParams<T>]: ResolveParams<T>[K] | number }
type ModalParamsArg<T extends ModalTo> =
  {} extends ModalParams<T> ? [params?: ModalParams<T>] : [params: ModalParams<T>]

// The dialog surface's exit is 100ms (`duration-100` in components/ui/dialog.tsx); the slot holds it a little longer.
const EXIT_MS = 150

/** The history entry a modal link pushed carries the slot path it opened, so closing it can go BACK. */
type ModalHistoryState = { modal?: string }

interface ResolvedSlot {
  path: string
  route: AnyRoute
  params: Record<string, unknown>
}

/** A slot path → its route, by the router's own matcher over the `/@modal` subtree. Junk or a failed parse → null. */
function resolveSlot(router: AnyRouter, path: unknown): ResolvedSlot | null {
  if (typeof path !== 'string' || !path.startsWith('/')) return null
  const [, rawParams, route] = router.getMatchedRoutes(PREFIX + path) as [unknown, Record<string, string>, AnyRoute?]
  // The matcher is fuzzy: an unknown tail comes back as the nearest ancestor with the rest in `**`.
  if (!route || !route.fullPath.startsWith(PREFIX + '/') || '**' in rawParams) return null
  const parse = route.options.params?.parse as ((raw: Record<string, string>) => Record<string, unknown>) | undefined
  try {
    return { path, route, params: parse ? { ...rawParams, ...parse(rawParams) } : rawParams }
  } catch {
    return null
  }
}

/**
 * Params for a slot route with numeric ids: a non-id is rejected, so `?@modal=/jobs/x/edit` matches nothing
 * instead of opening a form for job NaN. They stay strings: slot routes are real routes, and a number here would
 * widen every page's `useParams({ strict: false }).jobId` to `string | number`.
 */
export function idParams<K extends string>(...keys: K[]) {
  return {
    parse: (raw: Record<K, string>) => {
      for (const key of keys) {
        const id = Number(raw[key])
        if (!Number.isSafeInteger(id) || id <= 0) throw new Error(`not an id: ${key}=${raw[key]}`)
      }
      return raw
    },
    stringify: (params: Record<K, string>) => params,
  }
}

// `to: '.'` types its reducer against every route's search; these only touch root keys, so they are cast once here.
function withModal(path: string | undefined) {
  return ((prev: RootSearch) => ({ ...prev, [MODAL_KEY]: path })) as never
}

function withState(pushed: string | undefined, extra?: Record<string, unknown>) {
  return ((prev: Record<string, unknown>) => ({ ...prev, ...extra, modal: pushed })) as never
}

/** `to` + params → the slot path (`/jobs/5/edit`), built by the router so params stringify like any route's. */
function useModalPath() {
  const router = useRouter()
  return <T extends ModalTo>(to: T, ...[params]: ModalParamsArg<T>) =>
    router.buildLocation({ to, params } as never).pathname.slice(PREFIX.length)
}

/**
 * Warms the slot route a `?@modal=` path names: `router.preloadRoute` fetches its component chunk and runs its
 * loader. The root loader calls this with its location's path, so any intent preload of a modal link covers the slot.
 */
export function preloadModalSlot(router: AnyRouter, path: unknown) {
  const slot = resolveSlot(router, path)
  if (slot) void router.preloadRoute({ to: PREFIX + slot.path } as never)
}

/** Warms a slot route from code — the menus, when they OPEN, which is earlier than any hover over a row. */
export function usePreloadModal() {
  const router = useRouter()
  return <T extends ModalTo>(to: T, ...[params]: ModalParamsArg<T>) => {
    void router.preloadRoute({ to, params } as never)
  }
}

/**
 * Props for a `<Link>`/`<ButtonLink>` that opens a slot route. Preloading is the router's own: its intent preload
 * of `.?@modal=…` runs the root loader, which preloads the slot route (`preloadModalSlot`). Once the RFC lands this
 * is `<Link to="/@modal/jobs/$jobId/edit" params={…}>`.
 */
export function useModalLink<T extends ModalTo>(to: T, ...[params]: ModalParamsArg<T>) {
  const path = useModalPath()(to, ...([params] as ModalParamsArg<T>))
  return { to: '.' as const, search: withModal(path), state: withState(path) }
}

export type ModalLinkProps = ReturnType<typeof useModalLink>

/** Intent handlers alone, for a trigger that cannot be a link (a button inside a row that already is one). */
export function useModalIntent<T extends ModalTo>(to: T, ...[params]: ModalParamsArg<T>) {
  const preload = usePreloadModal()
  const onIntent = () => preload(to, ...([params] as ModalParamsArg<T>))
  return { onMouseEnter: onIntent, onFocus: onIntent, onTouchStart: onIntent }
}

/**
 * Opens a slot route imperatively, for triggers that cannot be a `<Link>` (menu rows, a mutation's success). A
 * `replace` open (an auto-open the user never asked for) leaves no history entry, so its close replaces too.
 */
export function useOpenModal() {
  const navigate = useNavigate()
  const modalPath = useModalPath()
  const preload = usePreloadModal()
  return <T extends ModalTo>(to: T, params: ModalParamsArg<T>[0], options?: { replace?: boolean }) => {
    const args = [params] as ModalParamsArg<T>
    preload(to, ...args)
    const path = modalPath(to, ...args)
    void navigate({
      to: '.',
      search: withModal(path),
      state: withState(options?.replace ? undefined : path),
      replace: options?.replace,
    })
  }
}

/**
 * Hands the open slot over to another route in the same history entry (a create form that continues into its
 * wizard). A pushed first route passes its entry on, so closing the second still goes back to the page.
 */
export function useSwapModal() {
  const router = useRouter()
  const navigate = useNavigate()
  const modalPath = useModalPath()
  const preload = usePreloadModal()
  return <T extends ModalTo>(to: T, params: ModalParamsArg<T>[0], state?: Record<string, unknown>) => {
    const args = [params] as ModalParamsArg<T>
    preload(to, ...args)
    const location = router.latestLocation
    const current = (location.search as Record<string, unknown>)[MODAL_KEY]
    const pushed = (location.state as ModalHistoryState).modal === current
    const path = modalPath(to, ...args)
    void navigate({
      to: '.',
      search: withModal(path),
      state: withState(pushed ? path : undefined, state),
      replace: true,
    })
  }
}

/**
 * The params of the open slot when it is `to`'s, else undefined — the RFC's
 * `useMatch({ from: '/@modal/…', shouldThrow: false })`.
 */
export function useModalMatch(to: ModalTo): Record<string, unknown> | undefined {
  const router = useRouter()
  const path = useSearch({
    from: '__root__',
    select: (search): unknown => (search as Record<string, unknown>)[MODAL_KEY],
  })
  const slot = resolveSlot(router, path)
  return slot?.route.fullPath === to ? slot.params : undefined
}

function useCloseModal() {
  const router = useRouter()
  const navigate = useNavigate()
  return (path: string) => {
    // `latestLocation`, not the committed one: a form that navigates away on submit and THEN closes has already moved on.
    const location = router.latestLocation
    if ((location.search as Record<string, unknown>)[MODAL_KEY] !== path) return
    if ((location.state as ModalHistoryState).modal === path) {
      router.history.back()
      return
    }
    // A deep link, or an auto-open: there is no entry of ours to go back to.
    void navigate({ to: '.', search: withModal(undefined), replace: true })
  }
}

interface ModalSlotValue {
  params: Record<string, unknown>
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ModalSlotContext = createContext<ModalSlotValue | null>(null)

/**
 * Inside a slot route's component: its params (parsed), and the open state the outlet drives. The RFC's
 * `Route.useParams()` — which cannot work here, since the main router never matches a slot route.
 */
export function useModalSlot<TRoute extends AnyRoute>(_route: TRoute) {
  const slot = use(ModalSlotContext)
  if (!slot) throw new Error('useModalSlot: rendered outside a <ModalSlotOutlet>')
  return slot as Omit<ModalSlotValue, 'params'> & { params: TRoute['types']['allParams'] }
}

interface Entry {
  slot: ResolvedSlot
  open: boolean
  /** The route's pending skeleton's enter, for the dialog to carry on (ui/dialog-surface.ts). */
  handover: EnterHandover
  /** Closed while its pending skeleton was up: the skeleton stays for the exit, and the dialog never renders. */
  skeletonExit?: boolean
}

/** The handovers whose pending skeleton is on screen right now. */
const skeletonsUp = new WeakSet<EnterHandover>()

/** Closes an entry; if its pending skeleton is what is on screen, that skeleton plays the exit. */
function closeEntry(entry: Entry): Entry {
  return entry.open ? { ...entry, open: false, skeletonExit: skeletonsUp.has(entry.handover) } : entry
}

/** Keeps a Suspense boundary on its fallback: the children of a slot whose skeleton is exiting. */
const NEVER = new Promise<never>(() => {})
function Suspended(): ReactNode {
  return use(NEVER)
}

/**
 * Renders the slot route whose path is in `?@modal=`, when it belongs to this outlet and its scope is matched. A
 * route that leaves the URL (a close, Back, a form that navigated away, a swap to another slot route) stays mounted
 * with `open={false}` for its exit animation; a path that comes back inside that window reopens the SAME instance.
 */
export function ModalSlotOutlet(props: { name?: 'userMenu' }) {
  const router = useRouter()
  const path = useSearch({
    from: '__root__',
    select: (search): unknown => (search as Record<string, unknown>)[MODAL_KEY],
  })
  const resolved = resolveSlot(router, path)
  const scope = resolved?.route.options.staticData?.slotScope
  const inScope = useMatches({ select: (matches) => !scope || matches.some((match) => match.routeId === scope) })
  const active = resolved && inScope && resolved.route.options.staticData?.slotOutlet === props.name ? resolved : null
  const activeKey = active?.path ?? null
  const close = useCloseModal()
  const preload = usePreloadModal()
  const [entries, setEntries] = useState<Entry[]>([])

  useEffect(() => {
    // A deep link opens here, after hydration, so SSR and the first client render agree; this also starts the chunk.
    if (active) preload(active.route.fullPath as ModalTo, active.params as never)
    // oxlint-disable-next-line react/set-state-in-effect -- animation timing: opening in the SAME commit that mounts the dialog (SSR, or a deep link hydrating) would skip its enter transition
    setEntries((current) => {
      const next = current.map((entry) => (entry.slot.path === activeKey ? entry : closeEntry(entry)))
      if (!active) return next
      if (!next.some((entry) => entry.slot.path === activeKey)) {
        return [...next, { slot: active, open: true, handover: createEnterHandover() }]
      }
      // Back inside the exit window: the SAME instance reopens.
      return next.map((entry) =>
        entry.slot.path === activeKey ? { ...entry, slot: active, open: true, skeletonExit: false } : entry,
      )
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on the open slot path alone
  }, [activeKey])

  const exiting = entries.some((entry) => !entry.open)
  useEffect(() => {
    if (!exiting) return
    const unmount = setTimeout(() => setEntries((current) => current.filter((entry) => entry.open)), EXIT_MS)
    return () => clearTimeout(unmount)
  }, [exiting, activeKey])

  return entries.map(({ slot, open, handover, skeletonExit }) => {
    const Component = slot.route.options.component as ComponentType
    const Pending = slot.route.options.pendingComponent as ComponentType | undefined
    const value: ModalSlotValue = {
      params: slot.params,
      open,
      onOpenChange: (next) => {
        if (next) return
        setEntries((current) => current.map((entry) => (entry.slot.path === slot.path ? closeEntry(entry) : entry)))
        close(slot.path)
      },
    }
    return (
      // oxlint-disable-next-line react/jsx-no-constructed-context-values -- one per entry, and the React Compiler memoizes it
      <ModalSlotContext key={slot.path} value={value}>
        <DialogEnterContext value={handover}>
          <Suspense
            fallback={
              (open || skeletonExit) && Pending ? (
                <SkeletonUp handover={handover}>
                  <Pending />
                </SkeletonUp>
              ) : null
            }
          >
            {skeletonExit ? <Suspended /> : <Component />}
          </Suspense>
        </DialogEnterContext>
      </ModalSlotContext>
    )
  })
}

/**
 * Inside a slot route's pendingComponent: the slot's params, and a close that works the way the dialog's own would,
 * so a skeleton's backdrop, Escape and close button answer before the dialog's code has arrived.
 */
export function useModalSlotPending() {
  const slot = use(ModalSlotContext)
  return { params: slot?.params ?? {}, open: slot?.open ?? false, close: () => slot?.onOpenChange(false) }
}

/** Marks the route's pending skeleton as the surface on screen, for a close to know it has to play the exit. */
function SkeletonUp(props: { handover: EnterHandover; children: ReactNode }) {
  const { handover } = props
  useLayoutEffect(() => {
    skeletonsUp.add(handover)
    return () => {
      skeletonsUp.delete(handover)
    }
  }, [handover])
  return props.children
}

/** What a dialog body takes from its slot route: the open state the outlet drives. */
export type ModalSlotProps = Omit<ModalSlotValue, 'params'>
